import { useCallback, useMemo, useState, type ReactNode } from 'react'
import { DiscoverySearchBar } from './DiscoverySearchBar'
import {
  DiscoverySearchContext,
  useDiscoverySearchControls,
  type DiscoverySearchConfig,
} from './useDiscoverySearch'

/**
 * Holds the search state for the discovery pages so the search bar can live in
 * the top header bar (below the tabs) while each page owns the query value and
 * its filtering.
 */
export function DiscoverySearchProvider({ children }: { children: ReactNode }) {
  const [query, setQuery] = useState('')
  const [config, setConfig] = useState<DiscoverySearchConfig | null>(null)

  const registerSearch = useCallback((next: DiscoverySearchConfig) => setConfig(next), [])
  const unregisterSearch = useCallback(() => {
    setConfig(null)
    setQuery('')
  }, [])

  const value = useMemo(
    () => ({ query, setQuery, config, registerSearch, unregisterSearch }),
    [query, config, registerSearch, unregisterSearch],
  )

  return <DiscoverySearchContext.Provider value={value}>{children}</DiscoverySearchContext.Provider>
}

/**
 * Renders the Airbnb-style search bar directly below the tabs, sharing the tabs'
 * centered column so the two line up on the same vertical axis. Renders nothing
 * until a page registers a search config, so non-discovery pages stay tabs-only.
 */
export function HeaderDiscoverySearch() {
  const ctx = useDiscoverySearchControls()
  if (!ctx?.config) return null

  return (
    <DiscoverySearchBar
      value={ctx.query}
      onChange={ctx.setQuery}
      placeholder={ctx.config.placeholder}
      ariaLabel={ctx.config.ariaLabel}
    />
  )
}
